// Settings, Renewal sheets: the words on the page and the checks made in the browser before the
// upload (the server checks again). Counts and status words only: nothing from a workbook is shown
// except the counts the server sends.
import type { SheetCounts } from '../auth/api';

export const maxSheetBytes = 20 * 1024 * 1024;
export const maxSheets = 3;

export const sheetCopy = {
  title: 'Renewal sheets',
  subtitle: 'Close renewals that the yearly renewal workbooks mark finished',
  cardTitle: 'Check the workbooks',
  cardSub: 'Nothing closes until you confirm',
  note: 'Choose one to three workbooks (.xlsx, 20 MB at most each). They are read on the server and never saved. The portal shows what would close, and closes nothing until you confirm.',
  dropTitle: 'Drag the workbooks here',
  dropLine: 'Excel files (.xlsx), up to 3',
  choose: 'Choose files',
  remove: 'Remove',
  check: 'Check workbooks',
  checking: 'Reading the workbooks...',
  kept: 'This check is kept for 15 minutes. Upload again after that.',
  nothing: 'Nothing would close. Nothing was saved.',
  uploadAgain: 'Upload again',
  uploadMore: 'Upload more workbooks',
  cancel: 'Cancel',
  expired:
    'This check is no longer available: it ran more than 15 minutes ago, or the portal was updated. Upload the workbooks again.',
  somethingWrong: 'Something went wrong. Try again.',
  offline: 'You are offline. Connect and try again.',
} as const;

const plural = (n: number, one: string, many: string) =>
  `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

export const closeButton = (n: number) => `Close ${plural(n, 'renewal', 'renewals')}`;

export const confirmTitle = (n: number) => `Close ${plural(n, 'renewal', 'renewals')}?`;
export const confirmLine =
  'Each one is set to Done or Cancelled, as its status in the workbook says, and logged as closed from the sheet. The portal checks every renewal again first and skips any that now has work in the portal. Each can be reopened from its Record.';

export const closedToast = (closed: number, planned: number) =>
  closed === planned
    ? `Closed ${plural(closed, 'renewal', 'renewals')} from the sheet.`
    : `Closed ${closed.toLocaleString('en-US')} of ${plural(planned, 'renewal', 'renewals')} from the sheet. ${plural(planned - closed, 'was', 'were')} skipped because ${planned - closed === 1 ? 'it changed' : 'they changed'} since the check.`;

const sum = (m: Record<string, number> | undefined) =>
  Object.values(m ?? {}).reduce((a, b) => a + b, 0);

/** "Completed/Done 2,400, Requoted/Bound 120", biggest first; the words come from the code's list. */
export function byStatus(m: Record<string, number> | undefined): string {
  return (
    Object.entries(m ?? {})
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .map(([k, n]) => `${k} ${n.toLocaleString('en-US')}`)
      .join(', ') || 'none'
  );
}

export type CountBox = { id: string; label: string; n: number; tone: string; line: string };

/** The four numbers of a workbook, in plain words. */
export function countBoxes(c: SheetCounts): CountBox[] {
  const stays = sum(c.openMatches) + c.unknownStatus;
  return [
    {
      id: 'close',
      label: 'Would close',
      n: sum(c.wouldClose),
      tone: 'n-green',
      line: byStatus(c.wouldClose),
    },
    {
      id: 'open',
      label: 'Stays open',
      n: stays,
      tone: 'n-ink',
      line: 'The sheet says they are still being worked',
    },
    {
      id: 'work',
      label: 'Done in the sheet, work in the portal',
      n: c.skippedPortalWork,
      tone: 'n-amber',
      line: 'A step is ticked in the portal, so they stay open',
    },
    {
      id: 'conflict',
      label: 'Conflicts',
      n: c.conflicts,
      tone: 'n-red',
      line:
        c.conflicts === 0
          ? 'No renewal has two rows'
          : `${plural(c.conflictRows, 'row', 'rows')} for ${plural(c.conflicts, 'renewal', 'renewals')}, never closed`,
    },
  ];
}

/** The smaller counts, one muted line (only the ones above zero). */
export function alsoLine(c: SheetCounts): string {
  const parts = [
    [c.alreadyClosed ?? 0, 'already closed in the portal'],
    [c.reopenedLeftOpen ?? 0, 'reopened in the portal, left open'],
    [sum(c.unmatched), 'rows with no renewal in the portal'],
    [c.ambiguous, 'rows that fit more than one renewal'],
    [c.skippedNoPolicyNumber + c.skippedNoDate, 'rows skipped for a missing policy number or date'],
  ] as const;
  const shown = parts.filter(([n]) => n > 0).map(([n, w]) => `${n.toLocaleString('en-US')} ${w}`);
  return shown.length ? `Also: ${shown.join(', ')}.` : '';
}

/**
 * What was not checked in a workbook, so "Nothing would close" is never read as "all checked": tabs
 * missing a needed column, hidden tabs, tabs without a table, and a workbook with no rows at all.
 */
export function tabWarnings(c: SheetCounts): string[] {
  const out = c.tabsSkipped.map(
    (t) =>
      `${t.tab === 'other tab' ? 'A tab that is not a month tab' : t.tab} was not checked: it has no ${t.missing.join(' or ')} column.`,
  );
  if (c.hiddenTabs > 0)
    out.push(`${plural(c.hiddenTabs, 'hidden tab', 'hidden tabs')} not checked.`);
  if (c.tabsWithoutTable > 0)
    out.push(`${plural(c.tabsWithoutTable, 'tab', 'tabs')} without a table not checked.`);
  if (c.rowsRead === 0) out.push('No rows were read from this workbook.');
  return out;
}

export const workbookTitle = (c: SheetCounts, i: number) =>
  `Workbook ${i + 1}${c.year ? ` (${c.year})` : ''}`;
export const workbookSub = (c: SheetCounts) =>
  `${plural(c.rowsRead, 'row', 'rows')} read from ${plural(c.tabsRead, 'tab', 'tabs')}`;

/** A file the browser can send, or the reason it cannot. */
export function pickSheetProblem(files: { name: string; size: number }[]): string | null {
  if (files.length > maxSheets) return `Choose up to ${maxSheets} workbooks at a time.`;
  for (const f of files) {
    if (!/\.xlsx$/i.test(f.name)) return 'Only Excel workbooks (.xlsx) can be checked.';
    if (f.size === 0) return 'A workbook is empty. Choose another file.';
    if (f.size > maxSheetBytes) return 'Each workbook can be 20 MB at most.';
  }
  return null;
}

/** Why the check did not go through, from the server's answer. */
export function sheetError(status: number, error: string, body: Record<string, unknown>): string {
  if (error === 'network') return sheetCopy.offline;
  if (error === 'workbook_refused') {
    const reason = typeof body.reason === 'string' ? body.reason : 'it could not be read';
    return `Workbook ${String(body.workbook ?? '')} was refused: ${reason}. Nothing was checked.`;
  }
  if (error === 'file_too_large' || error === 'too_large')
    return 'Each workbook can be 20 MB at most.';
  if (error === 'bad_file_count') return `Choose from 1 to ${maxSheets} workbooks.`;
  if (error === 'busy') return 'Another check is being read. Try again in a minute.';
  if (status === 403) return 'Only a Manager or an Admin can check the renewal sheets.';
  return sheetCopy.somethingWrong;
}
